import { api } from "@/lib/api";
import { AudioLanguageVariantDraft, StudioContentStatus } from "../schema";

export interface AudioMetadata {
  categoryId: string;
  tagOptionIds?: string[];
  primaryLanguage?: string;
  internalNotes?: string | null;
}

export interface AudioVariant extends AudioLanguageVariantDraft {
  id?: string;
}

export interface CreateAudioPayload {
  audio: AudioMetadata;
  variants: AudioVariant[];
}

export interface UpdateAudioPayload {
  audio: AudioMetadata;
  variants: AudioVariant[];
}

export interface AudioListItem {
  id: string;
  versionId: string;
  categoryId: string;
  tagOptionIds: string[];
  primaryTitle: string;
  titles?: string[];
  languageCodes: string[];
  coachIds: string[];
  coaches: Array<{ id: string; name: string | null; avatarUrl: string | null }>;
  status: StudioContentStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    name: string;
    roles: string[];
  };
}

export interface AudioDetail {
  id: string;
  versionId: string;
  categoryId: string;
  tagOptionIds: string[];
  internalNotes?: string;
  status: StudioContentStatus;
  version: number;
  rawFileHash?: string;
  mediaConvertError?: string | null;
  variants: AudioVariant[];
  creator?: {
    id: string;
    name: string;
    roles: string[];
  };
}

export interface AudioPickerItem {
  audioContentId: string;
  audioVersionId: string;
  localizedTitle: string;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
}

export interface AudioPickerQuery {
  categoryId?: string;
  tagOptionIds?: string[];
  languageCode: string;
}

export const audioService = {
  async list(filters?: {
    search?: string;
    status?: StudioContentStatus;
    categoryId?: string;
    tagId?: string;
    coachId?: string;
    language?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: AudioListItem[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.tagId) params.set("tagId", filters.tagId);
    if (filters?.coachId) params.set("coachId", filters.coachId);
    if (filters?.language) params.set("language", filters.language);
    if (filters?.page) params.set("page", filters.page.toString());
    if (filters?.limit) params.set("limit", filters.limit.toString());

    return api.get<{ items: AudioListItem[]; pagination: any }>(
      `/audio?${params.toString()}`,
    );
  },

  async get(id: string): Promise<AudioDetail> {
    return api.get<AudioDetail>(`/audio/${id}`);
  },

  async getByVersionId(versionId: string): Promise<AudioDetail> {
    return api.get<AudioDetail>(`/audio/version/${versionId}`);
  },

  async create(
    payload: CreateAudioPayload,
  ): Promise<{ id: string; versionId: string }> {
    return api.post<{ id: string; versionId: string }>("/audio", payload);
  },

  async update(
    versionId: string,
    payload: UpdateAudioPayload,
  ): Promise<{ id: string; versionId: string }> {
    return api.patch<{ id: string; versionId: string }>(
      `/audio/version/${versionId}`,
      payload,
    );
  },

  async submit(audioId: string): Promise<{
    success: boolean;
    audioId: string;
    versionId: string;
    status: string;
  }> {
    return api.post<{
      success: boolean;
      audioId: string;
      versionId: string;
      status: string;
    }>(`/audio/${audioId}/submit`, {});
  },

  async archive(audioId: string): Promise<{
    success: boolean;
    audioId: string;
    versionId: string;
    status: string;
  }> {
    return api.post<{
      success: boolean;
      audioId: string;
      versionId: string;
      status: string;
    }>(`/audio/${audioId}/archive`, {});
  },

  async restore(audioId: string): Promise<{
    success: boolean;
    audioId: string;
    versionId: string;
    status: string;
  }> {
    return api.post<{
      success: boolean;
      audioId: string;
      versionId: string;
      status: string;
    }>(`/audio/${audioId}/restore`, {});
  },

  async createNewVersion(
    audioId: string,
  ): Promise<{ id: string; versionId: string; version: number }> {
    return api.post<{ id: string; versionId: string; version: number }>(
      `/audio/${audioId}/new-version`,
      {},
    );
  },

  async picker({
    categoryId,
    tagOptionIds,
    languageCode,
  }: AudioPickerQuery): Promise<AudioPickerItem[]> {
    const params = new URLSearchParams();
    if (categoryId) params.set("categoryId", categoryId);
    params.set("languageCode", languageCode);
    if (tagOptionIds && tagOptionIds.length > 0) {
      params.set("tagOptionIds", tagOptionIds.join(","));
    }
    return api.get<AudioPickerItem[]>(`/audio/picker?${params.toString()}`);
  },
};
